// src/services/catchDiary.ts

import {
  collection,
  doc,
  deleteDoc,
  getDoc,
  getDocs,
  onSnapshot,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';

import { db } from '../lib/firebase';
import { deleteCatchStorageImages } from './imageUpload';
import type { CommunityPost, LogEntry } from '../types';

// -----------------------------------------------------------------------------
// Canonical Catch Diary
//
// New writes:
//   users/{uid}/catchDiary/{catchId}
//
// Temporary legacy reads:
//   catchDiary/{uid}/entries
//   legacy in-memory catchLog supplied by App
//
// FishIQ ingestion happens from the canonical catchDiary backend trigger.
// This client service does NOT manually sync catches into FishIQ.
// -----------------------------------------------------------------------------

export type CatchDiaryEntry = Partial<LogEntry> & {
  id: string;
  userId: string;

  text?: string | null;
  caption?: string | null;
  authorName?: string | null;

  imageUrl?: string | null;
  image?: string | null;
  images?: string[];
  imageUrls?: string[];

  sourcePostId?: string | null;
  source?: string | null;
  createdFrom?: 'community' | 'log' | string;

  timestamp?: number | null;
  createdAt?: any;
  updatedAt?: any;

  fishIQProcessed?: boolean;
  fishIQScore?: number | null;
  fishIQSummary?: string | null;

  [key: string]: any;
};

/**
 * UI-facing normalized catch timeline entry.
 *
 * `source` remains temporarily for compatibility with the current
 * CatchHistoryView filters. Once old catchLog records are migrated into
 * catchDiary, we can remove the log/diary distinction entirely.
 */
export interface CatchTimelineEntry {
  id: string;
  source: 'log' | 'diary';
  createdAt: Date;
  speciesName?: string;
  locationName?: string;
  photoUrl?: string | null;
  notes?: string | null;
  raw: any;
}

const LEGACY_CATCH_DIARY_SUBCOLLECTION = 'entries';

// -----------------------------------------------------------------------------
// Generic helpers
// -----------------------------------------------------------------------------

const getPostImages = (p: any): string[] => {
  if (Array.isArray(p?.images) && p.images.length) {
    return p.images.filter(Boolean);
  }

  if (Array.isArray(p?.imageUrls) && p.imageUrls.length) {
    return p.imageUrls.filter(Boolean);
  }

  const legacy = p?.imageUrl ?? p?.image ?? null;

  if (typeof legacy === 'string' && legacy) {
    return [legacy];
  }

  return [];
};

const toMillis = (value: any): number => {
  if (
    typeof value === 'number' &&
    Number.isFinite(value)
  ) {
    return value;
  }

  if (
    value &&
    typeof value.toMillis === 'function'
  ) {
    try {
      const millis = value.toMillis();

      if (
        typeof millis === 'number' &&
        Number.isFinite(millis)
      ) {
        return millis;
      }
    } catch {}
  }

  if (value instanceof Date) {
    return value.getTime();
  }

  if (typeof value === 'string') {
    const parsed = Date.parse(value);

    if (!Number.isNaN(parsed)) {
      return parsed;
    }
  }

  return 0;
};

const toDate = (value: any): Date | null => {
  if (
    value === null ||
    value === undefined ||
    value === ''
  ) {
    return null;
  }

  if (typeof value?.toDate === 'function') {
    try {
      return value.toDate();
    } catch {}
  }

  const millis = toMillis(value);

  if (!millis) {
    return null;
  }

  const date = new Date(millis);

  return Number.isNaN(date.getTime())
    ? null
    : date;
};

const normalizeDiaryDoc = (input: {
  id: string;
  data: any;
}): CatchDiaryEntry => {
  const data: any = input.data || {};

  return {
    ...data,
    id: data?.id ?? input.id,
  } as CatchDiaryEntry;
};

const finalSortNewestFirst = (
  rows: CatchDiaryEntry[],
) => {
  rows.sort((a: any, b: any) => {
    const at =
      toMillis(a?.timestamp) ||
      toMillis(a?.createdAt);

    const bt =
      toMillis(b?.timestamp) ||
      toMillis(b?.createdAt);

    return bt - at;
  });
};

// -----------------------------------------------------------------------------
// Timeline normalization
// -----------------------------------------------------------------------------

const extractSpeciesName = (
  raw: any,
): string | undefined =>
  raw?.speciesName ||
  raw?.species ||
  raw?.fishName ||
  raw?.title ||
  (typeof raw?.text === 'string' &&
    raw.text.trim()) ||
  undefined;

const extractLocationName = (
  raw: any,
): string | undefined =>
  raw?.locationName ||
  raw?.location ||
  raw?.waterName ||
  raw?.spotName ||
  raw?.placeName ||
  undefined;

const extractPhotoUrl = (
  raw: any,
): string | null | undefined =>
  raw?.photoUrl ||
  raw?.imageUrl ||
  raw?.photoURL ||
  raw?.image ||
  raw?.photo?.url ||
  (Array.isArray(raw?.images)
    ? raw.images.find(Boolean)
    : null) ||
  (Array.isArray(raw?.imageUrls)
    ? raw.imageUrls.find(Boolean)
    : null) ||
  null;

const extractNotes = (
  raw: any,
): string | undefined =>
  raw?.notes ||
  raw?.description ||
  raw?.caption ||
  (typeof raw?.text === 'string' &&
    raw.text.trim()) ||
  undefined;

const normalizeKeyPart = (
  value: any,
): string =>
  String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .replace(/[^a-z0-9 .,_-]/g, '');

const scoreEntryRichness = (
  entry: CatchTimelineEntry,
): number => {
  const raw: any = entry?.raw || {};

  let score = 0;

  // Prefer canonical diary records over legacy catchLog copies.
  if (entry.source === 'diary') {
    score += 3;
  }

  if (entry.photoUrl) score += 2;
  if (entry.notes) score += 1;
  if (raw?.weight) score += 1;

  if (
    raw?.rod ||
    raw?.reel ||
    raw?.lureOrBait ||
    raw?.equipmentSummary
  ) {
    score += 2;
  }

  if (
    Array.isArray(raw?.images) &&
    raw.images.length
  ) {
    score += 2;
  }

  if (
    Array.isArray(raw?.imageUrls) &&
    raw.imageUrls.length
  ) {
    score += 2;
  }

  return score;
};

const buildTimelineDedupKey = (
  entry: CatchTimelineEntry,
): string => {
  const raw: any = entry?.raw || {};

  const sourceRef = normalizeKeyPart(
    raw?.id ||
      raw?.catchId ||
      raw?.sourceId ||
      raw?.sourcePostId ||
      raw?.communityPostId ||
      '',
  );

  if (sourceRef) {
    return `src:${sourceRef}`;
  }

  const species = normalizeKeyPart(
    entry?.speciesName ||
      raw?.species ||
      raw?.speciesName ||
      '',
  );

  const location = normalizeKeyPart(
    entry?.locationName ||
      raw?.location ||
      raw?.locationName ||
      '',
  );

  const photo = normalizeKeyPart(
    entry?.photoUrl ||
      raw?.imageUrl ||
      raw?.image ||
      '',
  );

  const weight = normalizeKeyPart(
    raw?.weight || '',
  );

  const minute = Math.floor(
    (entry.createdAt?.getTime?.() || 0) /
      60000,
  );

  return [
    species,
    location,
    weight,
    photo,
    minute,
  ].join('|');
};

const normalizeTimelineEntry = (
  raw: any,
  source: 'log' | 'diary',
): CatchTimelineEntry => {
  const createdAt =
    toDate(raw?.timestamp) ||
    (typeof raw?.date === 'string' &&
    typeof raw?.time === 'string'
      ? toDate(
          `${raw.date}T${raw.time}`,
        )
      : null) ||
    toDate(raw?.catchTimestamp) ||
    toDate(raw?.createdAt) ||
    new Date();

  return {
    id:
      String(
        raw?.id ||
          raw?.catchId ||
          raw?.sourceId ||
          '',
      ).trim() ||
      `${source}_${createdAt.getTime()}`,

    source,
    createdAt,

    speciesName:
      extractSpeciesName(raw),

    locationName:
      extractLocationName(raw),

    photoUrl:
      extractPhotoUrl(raw),

    notes:
      extractNotes(raw),

    raw,
  };
};

/**
 * Transitional timeline builder.
 *
 * Canonical diary records are preferred.
 * Legacy catchLog records are included only so old user data does not disappear
 * before we migrate it into catchDiary.
 */
export function buildCatchTimelineEntries(input: {
  diaryEntries?: CatchDiaryEntry[] | null;
  legacyLogEntries?: LogEntry[] | null;
}): CatchTimelineEntry[] {
  const combined: CatchTimelineEntry[] = [];

  const legacyLogs = Array.isArray(
    input.legacyLogEntries,
  )
    ? input.legacyLogEntries
    : [];

  const diaryEntries = Array.isArray(
    input.diaryEntries,
  )
    ? input.diaryEntries
    : [];

  for (const raw of legacyLogs) {
    combined.push(
      normalizeTimelineEntry(raw, 'log'),
    );
  }

  for (const raw of diaryEntries) {
    combined.push(
      normalizeTimelineEntry(raw, 'diary'),
    );
  }

  combined.sort(
    (a, b) =>
      b.createdAt.getTime() -
      a.createdAt.getTime(),
  );

  const deduped = new Map<
    string,
    CatchTimelineEntry
  >();

  for (const entry of combined) {
    const key =
      buildTimelineDedupKey(entry);

    const existing = deduped.get(key);

    if (!existing) {
      deduped.set(key, entry);
      continue;
    }

    const nextScore =
      scoreEntryRichness(entry);

    const existingScore =
      scoreEntryRichness(existing);

    if (nextScore > existingScore) {
      deduped.set(key, entry);
      continue;
    }

    if (
      nextScore === existingScore &&
      entry.createdAt.getTime() >
        existing.createdAt.getTime()
    ) {
      deduped.set(key, entry);
    }
  }

  return Array.from(
    deduped.values(),
  ).sort(
    (a, b) =>
      b.createdAt.getTime() -
      a.createdAt.getTime(),
  );
}

// -----------------------------------------------------------------------------
// Writes
// -----------------------------------------------------------------------------

/**
 * Save a community post into the canonical catch diary.
 *
 * Canonical write:
 * users/{uid}/catchDiary/{catchId}
 */
export async function addDiaryEntryFromPost(
  userId: string,
  post: CommunityPost,
): Promise<void> {
  const diaryCol = collection(
    db,
    'users',
    userId,
    'catchDiary',
  );

  const entryRef = doc(diaryCol);

  const p: any = post;

  const images = getPostImages(p);
  const primary = images[0] ?? null;

  const ts =
    typeof p?.timestamp === 'number'
      ? p.timestamp
      : toMillis(p?.createdAt) ||
        Date.now();

  const payload: CatchDiaryEntry = {
    id: entryRef.id,
    userId,

    sourcePostId:
      p?.id ??
      p?.sourcePostId ??
      null,

    source: 'community_post',
    createdFrom: 'community',

    text:
      p?.text ??
      p?.caption ??
      '',

    authorName:
      p?.authorName ??
      p?.author ??
      null,

    ...(primary
      ? {
          imageUrl: primary,
          image: primary,
        }
      : {}),

    ...(images.length
      ? {
          images,
          imageUrls: images,
        }
      : {}),

    species: p?.species ?? '',
    weight: p?.weight ?? '',
    location: p?.location ?? '',

    technique:
      p?.technique ?? '',

    lureOrBait:
      p?.lureOrBait ??
      p?.lure ??
      '',

    lureOrBaitType:
      p?.lureOrBaitType ?? '',

    weather:
      p?.weather ?? '',

    waterCondition:
      p?.waterCondition ??
      p?.conditions ??
      '',

    conditions:
      p?.conditions ??
      p?.waterCondition ??
      '',

    waterType:
      p?.waterType ?? '',

    waterName:
      p?.waterName ??
      p?.location ??
      '',

    catchTimestamp:
      p?.catchTimestamp ?? ts,

    catchDate:
      p?.catchDate ?? '',

    catchTime:
      p?.catchTime ?? '',

    timeBucket:
      p?.timeBucket ??
      p?.communitySignal?.timeBucket ??
      '',

    communitySignal:
      p?.communitySignal ?? null,

    timestamp: ts,

    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),

    fishIQProcessed: false,
    fishIQScore: null,
    fishIQSummary: null,
  };

  await setDoc(
    entryRef,
    payload as any,
  );
}


/**
 * Delete one canonical catch diary record.
 *
 * Community post handling intentionally lives in the Community service so its
 * established soft-delete/audit behavior is preserved.
 */
export async function deleteCanonicalCatch(input: {
  userId: string;
  catchId: string;
}): Promise<{ mediaCleanupFailed: boolean }> {
  const userId = String(input.userId || '').trim();
  const catchId = String(input.catchId || '').trim();
  if (!userId || !catchId) throw new Error('Missing catch id.');

  const ref = doc(db, 'users', userId, 'catchDiary', catchId);
  const snap = await getDoc(ref);
  const data: any = snap.exists() ? snap.data() : null;

  if (data?.tournamentEntry || data?.tournamentId || data?.tournamentSubmissionId) {
    throw new Error('Tournament catches are locked to preserve submitted evidence.');
  }

  const urls = Array.from(
    new Set(
      [
        ...(Array.isArray(data?.images) ? data.images : []),
        ...(Array.isArray(data?.imageUrls) ? data.imageUrls : []),
        data?.imageUrl,
        data?.image,
        data?.photoUrl,
      ]
        .map((value) => String(value || '').trim())
        .filter(Boolean),
    ),
  );

  // Delete the canonical Firestore record first. FishIQ cleanup triggers are keyed
  // off this deletion, and a storage cleanup failure must never resurrect the catch.
  await deleteDoc(ref);

  // Only remove private/catch-owned media here. Community media remains when the
  // angler chooses "delete from Roddo Log only" so an existing Baleen Bay post
  // cannot be left with a broken image.
  const cleanup = await deleteCatchStorageImages({
    uid: userId,
    catchId,
    urls,
    includeCommunity: false,
  });

  return { mediaCleanupFailed: cleanup.failed > 0 };
}

export async function deleteLegacyCatchLogEntry(input: {
  userId: string;
  catchId: string;
}): Promise<void> {
  const userId = String(input.userId || '').trim();
  const catchId = String(input.catchId || '').trim();
  if (!userId || !catchId) throw new Error('Missing catch id.');

  await deleteDoc(doc(db, 'users', userId, 'catchLog', catchId));
}

// -----------------------------------------------------------------------------
// Reads
// -----------------------------------------------------------------------------

/**
 * Reads:
 *
 * 1. users/{uid}/catchDiary
 * 2. catchDiary/{uid}/entries (temporary legacy compatibility)
 *
 * Canonical records always win when IDs collide.
 */
export async function fetchCatchDiaryEntries(
  userId: string,
): Promise<CatchDiaryEntry[]> {
  const canonicalById = new Map<
    string,
    CatchDiaryEntry
  >();

  const legacyById = new Map<
    string,
    CatchDiaryEntry
  >();

  {
    const diaryCol = collection(
      db,
      'users',
      userId,
      'catchDiary',
    );

    const snap = await getDocs(diaryCol);

    for (const d of snap.docs) {
      const row = normalizeDiaryDoc({
        id: d.id,
        data: d.data(),
      });

      canonicalById.set(
        row.id,
        row,
      );
    }
  }

  {
    const legacyCol = collection(
      db,
      'catchDiary',
      userId,
      LEGACY_CATCH_DIARY_SUBCOLLECTION,
    );

    const snap = await getDocs(legacyCol);

    for (const d of snap.docs) {
      const row = normalizeDiaryDoc({
        id: d.id,
        data: d.data(),
      });

      legacyById.set(
        row.id,
        row,
      );
    }
  }

  const merged = new Map(
    legacyById,
  );

  for (const [
    id,
    row,
  ] of canonicalById) {
    merged.set(id, row);
  }

  const rows = Array.from(
    merged.values(),
  );

  finalSortNewestFirst(rows);

  return rows;
}

// -----------------------------------------------------------------------------
// Live subscription
// -----------------------------------------------------------------------------

export function subscribeToCatchDiaryEntries(
  userId: string,
  onRows: (
    rows: CatchDiaryEntry[],
  ) => void,
  onError?: (
    err: unknown,
  ) => void,
): () => void {
  let canonicalById = new Map<
    string,
    CatchDiaryEntry
  >();

  let legacyById = new Map<
    string,
    CatchDiaryEntry
  >();

  const emit = () => {
    // Legacy first, canonical overwrites duplicate IDs.
    const merged = new Map(
      legacyById,
    );

    for (const [
      id,
      row,
    ] of canonicalById) {
      merged.set(id, row);
    }

    const rows = Array.from(
      merged.values(),
    );

    finalSortNewestFirst(rows);

    onRows(rows);
  };

  const unsubCanonical = onSnapshot(
    collection(
      db,
      'users',
      userId,
      'catchDiary',
    ),

    (snap) => {
      const next = new Map<
        string,
        CatchDiaryEntry
      >();

      for (const d of snap.docs) {
        const row =
          normalizeDiaryDoc({
            id: d.id,
            data: d.data(),
          });

        next.set(row.id, row);
      }

      canonicalById = next;
      emit();
    },

    (err) => {
      if (onError) {
        onError(err);
      } else {
        console.error(
          '[catchDiary] subscribe canonical error',
          err,
        );
      }
    },
  );

  const unsubLegacy = onSnapshot(
    collection(
      db,
      'catchDiary',
      userId,
      LEGACY_CATCH_DIARY_SUBCOLLECTION,
    ),

    (snap) => {
      const next = new Map<
        string,
        CatchDiaryEntry
      >();

      for (const d of snap.docs) {
        const row =
          normalizeDiaryDoc({
            id: d.id,
            data: d.data(),
          });

        next.set(row.id, row);
      }

      legacyById = next;
      emit();
    },

    (err) => {
      if (onError) {
        onError(err);
      } else {
        console.error(
          '[catchDiary] subscribe legacy error',
          err,
        );
      }
    },
  );

  return () => {
    unsubCanonical();
    unsubLegacy();
  };
}